import { useMemo } from "react";
import { useEvento } from "@/store/evento/evento";
import { formatterArg, round } from "@/helpers/currency";
import { useOccupiedSeats } from "./useOccupiedSeats";
import { useReservedStock } from "./useReservedStock";
import { useStockConflict } from "./useStockConflict";
import { MapaListItem } from "@/lib/services/mapas";
import { COSTE_DE_SERVICIO } from "@/config/facturacion";

export function useComprarButton(mapa?: MapaListItem | null) {
  const { state: funcion } = useEvento.use.funcion();
  const { state: entradas } = useEvento.use.entradas();
  const { state: activeTab } = useEvento.use.tabs();

  const { seats: occupiedSeats, loading: loadingSeats } = useOccupiedSeats(mapa ? funcion?.id : null);

  const { stock: stockReservado, loading: loadingStock } = useReservedStock(!mapa ? funcion?.id : null);

  const total = useMemo(() => {
    if (!funcion) return 0;
    return entradas.reduce((prev, entrada) => prev + (entrada.precio * entrada.asientos.length), 0);
  }, [funcion, entradas]);

  const totalWithServiceFee = useMemo(() => {
    return round(total * (COSTE_DE_SERVICIO + 1));
  }, [total]);

  // Detectar conflictos de asientos ocupados (eventos CON mapa)
  const hasSeatConflicts = useMemo(() => {
    if (!mapa) return false; // Solo aplica para eventos con mapa
    if (!entradas || entradas.length === 0) return false;
    if (occupiedSeats.length === 0) return false;

    const occupiedSet = new Set(occupiedSeats);

    const conflicts = entradas.some((entrada) =>
      entrada.asientos?.some((asiento) => occupiedSet.has(asiento.seatId))
    );

    return conflicts;
  }, [mapa, entradas, occupiedSeats]);

  // Detectar conflictos de stock (eventos SIN mapa)
  const { hasConflict: hasStockConflict } = useStockConflict(entradas, stockReservado);

  // Conflicto general (cualquiera de los dos casos)
  const hasConflicts = hasSeatConflicts || hasStockConflict;

  const message = useMemo(() => {
    if (activeTab !== 'funciones') return 'COMPRAR';
    if (!funcion) return 'SELECCIONE UNA FUNCIÓN';
    if (hasSeatConflicts) return 'ASIENTOS NO DISPONIBLES';
    if (hasStockConflict) return 'STOCK INSUFICIENTE';
    if (entradas.length < 1) return mapa ? 'SELECCIONE ASIENTOS' : 'SELECCIONE ENTRADAS';
    if (total === 0) return 'GRATIS';
    return `COMPRAR ${formatterArg.format(total)}`;
  }, [activeTab, funcion, entradas.length, total, hasSeatConflicts, hasStockConflict, mapa]);


  const canContinue = useMemo(() => {
    // Si está cargando datos, no puede continuar todavía
    if (loadingSeats || loadingStock) return false;

    // Si hay conflictos (asientos o stock), no puede continuar
    if (hasConflicts) return false;

    const hasEntradas = entradas.some(e => e.asientos.length > 0)
    const hasFuncion = funcion !== null

    return (hasEntradas && hasFuncion) || (!hasFuncion);

  }, [entradas, funcion, hasConflicts, loadingSeats, loadingStock]);

  return { total, totalWithServiceFee, message, canContinue, hasConflicts }
}
